import { TeamName } from "@/components/TeamName";
import { useModals } from "@/features/modals/hooks";
import { useActiveProject } from "@/features/project/ActiveProject";
import { ProjectQuickInfo } from "@/features/project/ProjectQuickInfo";
import { getTaskTitle, shouldAllowActions } from "@/features/tasks/utils";
import { type History, type Task } from "@repo/api";
import {
  Accordion,
  AccordionItem,
  Button,
  ListView,
  ListViewItem,
  Text,
  Variables,
  checkmarkSmallIcon,
  clipboardCheckmarkIcon,
  clockIcon,
  documentAddIcon,
  pencilIcon,
  personIcon,
  shoppingBagIcon,
  trashCanIcon,
  warehouseIcon,
} from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  ButtonsContainer,
  CoWorkerName,
  CustomerCardWrapper,
  FlexDiv,
  LocalizedDateAndTime,
  LocalizedDateRange,
  LocalizedDateTimeRange,
  SectionContainer,
  getIsCompletable,
  getTaskIcon,
  localizeDateAndTime,
  translateTaskType,
  useSettings,
} from "@repo/ui";
import { combineDates, trackAnalytics } from "@repo/utils";
import { type ReactNode } from "react";

import { TaskHistories } from "./TaskHistories";
import {
  type TaskModalState,
  type TaskModalStateActions,
} from "./TaskModalReducer";

export interface TaskModalViewProps {
  state: TaskModalState;
  actions: TaskModalStateActions;
  task?: Task;
  historyEntries?: History[];
  onMarkAsDone: (theTask: Task) => void;
  onDelete: () => void;
  taskDetails?: ReactNode;
}

export const TaskModalView = ({
  state,
  actions,
  task,
  historyEntries,
  onMarkAsDone,
  onDelete,
  taskDetails,
}: TaskModalViewProps) => {
  const { pushModal } = useModals();

  const { dateFnsLocale } = useTranslation();
  const { language, task_configuration } = useSettings();
  const { opportunity } = useActiveProject();

  const allowActions = shouldAllowActions(task);

  const renderField = {
    type: task?.parentTaskType && task.subTaskType !== "preCallTask",
    subType: task?.subTaskType,
    dateAndTime: !task?.isTaskDueBy,
    dueDate: task?.isTaskDueBy,
    created: task?.created?.createdBy && task.created.taskCreatedDateTime,
    appointment: task?.appointmentNumber,
    project: !opportunity && task?.opportunityId,
    history: task?.opportunityId,
    customer: task?.opportunityId !== undefined,
    comment: state.description,
  };

  return (
    <SectionContainer>
      <FlexDiv justifyContent="space-between" gap={`${Variables.space50}`}>
        <Text textStyle="Heading.M">{getTaskTitle(task)}</Text>

        <ButtonsContainer>
          {allowActions && (
            <>
              <Button
                size="small"
                iconOnly
                ssrIcon={pencilIcon}
                title={t("action.edit")}
                onClick={() => {
                  actions.setMode("EDIT");
                  trackAnalytics("edit_task");
                }}
                disabled={state.isDone}
              />

              <Button
                size="small"
                iconOnly
                ssrIcon={checkmarkSmallIcon}
                title={t("action.mark_done")}
                onClick={() => {
                  if (task) {
                    onMarkAsDone(task);
                  }
                  trackAnalytics("task_mark_done");
                }}
                disabled={
                  !task ||
                  state.isDone ||
                  !getIsCompletable(
                    state.parentTaskType,
                    task_configuration?.task_types
                  )
                }
                loading={state.loadingState === "UPDATING"}
              />
            </>
          )}
          <Button
            size="small"
            iconOnly
            ssrIcon={trashCanIcon}
            title={t("action.delete")}
            onClick={() => {
              pushModal({
                type: "CONFIRM",
                onConfirm: onDelete,
                title: t("action.delete_task"),
                message: t("prompt.confirm_delete_task"),
              });
              trackAnalytics("confirm_delete_task");
            }}
            disabled={!task?.id || state.isDone}
            loading={state.loadingState === "DELETING"}
          />
        </ButtonsContainer>
      </FlexDiv>

      <ListView id="calendar-task-details" showDivider={false}>
        {renderField.dueDate && (
          <ListViewItem
            leadingIcon={clockIcon}
            title={t("label.due_date")}
            description={
              state.dueDate ? (
                <LocalizedDateAndTime
                  date={combineDates(state.dueDate, state.dueTime)}
                />
              ) : (
                "-"
              )
            }
          />
        )}
        {renderField.dateAndTime && (
          <ListViewItem
            leadingIcon={clockIcon}
            title={t("label.date_and_time")}
            description={
              state.startDate ? (
                state.allDay ? (
                  <LocalizedDateRange
                    fromDate={state.startDate}
                    toDate={state.endDate}
                  />
                ) : (
                  <LocalizedDateTimeRange
                    fromDate={combineDates(state.startDate, state.startTime)}
                    toDate={
                      state.endDate
                        ? combineDates(state.endDate, state.endTime)
                        : undefined
                    }
                  />
                )
              ) : (
                "-"
              )
            }
          />
        )}

        {renderField.type && (
          <ListViewItem
            leadingIcon={clipboardCheckmarkIcon}
            title={t("label.type")}
            description={translateTaskType(
              language,
              state.parentTaskType,
              undefined,
              task_configuration?.task_types
            )}
          />
        )}
        {renderField.subType && (
          <ListViewItem
            leadingIcon={getTaskIcon(
              state.parentTaskType,
              state.subTaskType,
              task_configuration?.task_types
            )}
            title={t("label.subtype")}
            description={translateTaskType(
              language,
              state.parentTaskType,
              state.subTaskType,
              task_configuration?.task_types
            )}
          />
        )}
        {task?.created?.createdBy && task.created.taskCreatedDateTime && (
          <ListViewItem
            leadingIcon={documentAddIcon}
            title={t("label.created_by")}
            description={<CoWorkerName email={task.created.createdBy} />}
            addOn={localizeDateAndTime(
              task.created.taskCreatedDateTime,
              dateFnsLocale
            )}
          />
        )}
        {task?.emailId && (
          <ListViewItem
            leadingIcon={personIcon}
            title={t("label.assigned_to")}
            description={<CoWorkerName email={task?.emailId} />}
          />
        )}
        {task?.assignedTeam && (
          <ListViewItem
            leadingIcon={personIcon}
            title={t("label.assigned_to")}
            description={<TeamName teamId={task.assignedTeam} />}
          />
        )}
        {task?.cmpCode && (
          <ListViewItem
            leadingIcon={warehouseIcon}
            title={t("label.cmp")}
            description={task.cmpName ?? task.cmpCode}
          />
        )}
        {task?.orderId && (
          <ListViewItem
            leadingIcon={shoppingBagIcon}
            title={t("label.order_id")}
            description={task.orderId}
          />
        )}
      </ListView>

      {taskDetails}

      <Accordion collapsible={false}>
        {renderField.customer && (
          <AccordionItem
            id="customer-details"
            title={t("label.customer_details")}
            open={true}
          >
            <CustomerCardWrapper
              editable={false}
              projectId={task?.opportunityId}
            />
          </AccordionItem>
        )}

        {renderField.project && (
          <ProjectQuickInfo projectId={task?.opportunityId} showTabs={false} />
        )}
        {renderField.comment && (
          <AccordionItem
            id="task-modal-comment"
            title={t("label.comment")}
            open={true}
          >
            <Text pre>{state.description}</Text>
          </AccordionItem>
        )}

        {renderField.history && (
          <AccordionItem id="task-modal-history" title={t("label.history")}>
            <TaskHistories task={task} historyEntries={historyEntries} />
          </AccordionItem>
        )}
      </Accordion>
    </SectionContainer>
  );
};
